import Image from 'next/image';
import styled from 'styled-components';
import { AiOutlineMore } from 'react-icons/ai';
import { devices } from '@src/styles/theme';
import { IComment, NestReplies } from '@src/interfaces/community';
import { useQuery } from '@tanstack/react-query';
import { authKeys } from '@src/keys/authKey';
import { auth } from '@src/services/api';
import { useInsertComment, useDeleteComment } from '@src/hooks/useCommunitys';
import { useCallback, useState } from 'react';
import { useRouter } from 'next/router';
import { useSetRecoilState } from 'recoil';
import { modalAtom } from '@src/recoils/atom/modal';
import { communintyDateFormat } from '@src/utils/date';
import useAuth from '@src/hooks/useAuth';
import { avatarInitial, avatarTint } from '@src/utils/chatAvatar';

type Props = {
  replies: Array<IComment>;
  communityId: string;
};

const Comment = ({ replies, communityId }: Props) => {
  const { fetchProfile } = useAuth();
  const { data: profile } = fetchProfile();
  const [commentStr, setCommentStr] = useState('');
  const [replyCommentStr, setReplyCommentStr] = useState('');
  const [selectReplyUuid, setSelectReplyUuid] = useState('');
  const router = useRouter();
  const type = router.pathname.startsWith('/community/board/detail')
    ? 'board'
    : 'donation';
  const { mutate: onComment } = useInsertComment(communityId, type);
  const { mutate: onDeleteComment } = useDeleteComment(communityId, type);
  const [update, setUpdate] = useState('');
  const setModal = useSetRecoilState(modalAtom);
  const onClickReply = useCallback(
    (id: string) => {
      // 답글쓰기를 두번 누르면 닫는다.
      if (selectReplyUuid !== '') {
        setSelectReplyUuid('');
        return;
      }
      // 답변 초기화
      if (replyCommentStr !== '') setReplyCommentStr('');
      setSelectReplyUuid(id);
    },
    [selectReplyUuid, replyCommentStr]
  );

  const onSubmitComment = useCallback(
    (isReply: boolean, id?: string) => {
      if (isReply) {
        const body: any = {
          requestData: {
            content: replyCommentStr,
            donationUuid: communityId,
            uuid: id,
          },
          isReply,
        };
        onComment(body);
        setReplyCommentStr('');
        setSelectReplyUuid('');
      } else {
        const body: any = {
          requestData: {
            content: commentStr,
            donationUuid: communityId,
            uuid: communityId,
          },
          isReply,
        };
        onComment(body);
        setCommentStr('');
      }
    },
    [commentStr, replyCommentStr]
  );

  const onChangeText = useCallback((isReply: boolean, value: string) => {
    if (isReply) {
      setReplyCommentStr(value);
    } else {
      setCommentStr(value);
    }
  }, []);

  const onSubmitDelete = useCallback((isReply: boolean, id?: string) => {
    if (isReply) {
      const body: any = {
        requestData: {
          content: '',
          donationUuid: communityId,
          uuid: id,
        },
        isReply,
      };
      onDeleteComment(body);
      setUpdate('');
    } else {
      const body: any = {
        requestData: {
          content: '',
          donationUuid: communityId,
          uuid: id,
        },
        isReply,
      };
      onDeleteComment(body);
      setUpdate('');
    }
  }, []);

  return (
    <>
      {/* 로그인을 했을때 답변리스트가 없는경우 */}
      {profile?.status === 'ACTIVE' && (
        <CommentContainer>
          {/* 디자인: '응원 댓글' + 건수 */}
          <CommentHeader>
            <CommentHeaderTitle>응원 댓글</CommentHeaderTitle>
            <CommentHeaderCount>{replies?.length ?? 0}</CommentHeaderCount>
          </CommentHeader>
          {
            // 답변 리스트
            replies?.map((reply: IComment, i: number) => (
              <div key={reply.uuid}>
                <FlexWidth100>
                  <CommentAvatar
                    url={reply.writer.profileImageUrl}
                    name={reply.writer.displayName}
                  />
                  <CommentTextWrap>
                    {profile?.uuid === reply.writer.uuid && (
                      <MoreIcon
                        onClick={() => {
                          setUpdate(update === '' ? reply.uuid : '');
                        }}
                      />
                    )}
                    {/* 디자인: [이름 · 시간] 한 줄 → 아래 본문 */}
                    <CommentMetaRow>
                        <CommentWriter>{reply.writer.displayName}</CommentWriter>
                        <CommentTime>{communintyDateFormat(reply?.createdAt)}</CommentTime>
                      </CommentMetaRow>
                    <CommentContent>{reply.content}</CommentContent>
                    {update === reply.uuid && (
                      <>
                        <CommentButton
                          isActive={true}
                          onClick={() => {
                            onSubmitDelete(false, reply.uuid);
                          }}
                        >
                          삭제
                        </CommentButton>
                      </>
                    )}
                  </CommentTextWrap>
                </FlexWidth100>
                <MarginBox
                  onClick={() => {
                    onClickReply(reply.uuid);
                  }}
                >
                  답글쓰기
                </MarginBox>
                {selectReplyUuid === reply.uuid && (
                  <FlexMargin
                    style={{
                      margin: '10px 0px 20px 50px',
                    }}
                  >
                    <CommentInput
                      value={replyCommentStr}
                      onChange={(e) => {
                        console.log(e.target.value);

                        onChangeText(true, e.target.value);
                      }}
                    />
                    <CommentButton
                      isActive={replyCommentStr.length >= 1}
                      onClick={() => {
                        onSubmitComment(true, reply.uuid);
                      }}
                    >
                      등록
                    </CommentButton>
                  </FlexMargin>
                )}
                {
                  // 답변의 답글
                  reply.nestedReplies.length > 0 &&
                    reply.nestedReplies.map((nestReply: NestReplies) => (
                      <FlexMargin
                        style={{
                          marginBottom: 20,
                        }}
                        key={nestReply.uuid}
                      >
                        <CommentAvatar
                          url={nestReply.writer.profileImageUrl}
                          name={nestReply.writer.displayName}
                        />
                        <CommentTextWrap>
                          {profile?.uuid === nestReply.writer.uuid && (
                            <MoreIcon
                              onClick={() => {
                                setUpdate(nestReply.uuid);
                              }}
                            />
                          )}
                          <CommentMetaRow>
                            <CommentWriter>{nestReply.writer.displayName}</CommentWriter>
                            <CommentTime>{communintyDateFormat(nestReply?.createdAt)}</CommentTime>
                          </CommentMetaRow>
                          <CommentContent>{nestReply.content}</CommentContent>
                          {update === nestReply.uuid && (
                            <>
                              <CommentButton
                                isActive={true}
                                onClick={() => {
                                  onSubmitDelete(true, nestReply.uuid);
                                }}
                              >
                                삭제
                              </CommentButton>
                            </>
                          )}
                        </CommentTextWrap>
                      </FlexMargin>
                    ))
                }
              </div>
            ))
          }
          {/* 디자인: 입력창은 목록 하단 */}
          <CommentInputRow>
            <CommentInput
              value={commentStr}
              placeholder={'따뜻한 한마디를 남겨주세요'}
              onChange={(e) => onChangeText(false, e.target.value)}
            />
            <CommentButton
              isActive={commentStr.length >= 1}
              onClick={() => {
                onSubmitComment(false);
              }}
            >
              등록
            </CommentButton>
          </CommentInputRow>
        </CommentContainer>
      )}

      {/* 로그인을 하지 않았을때  */}
      {profile?.status !== 'ACTIVE' && (
        <CommentContainer>
          {/* 디자인: '응원 댓글' + 건수 */}
          <CommentHeader>
            <CommentHeaderTitle>응원 댓글</CommentHeaderTitle>
            <CommentHeaderCount>{replies?.length ?? 0}</CommentHeaderCount>
          </CommentHeader>
          {
            // 답변이 있을경우 답변리스트만 출력한다.
            replies?.length > 0 &&
              // 답변 리스트
              replies.map((reply: IComment, i: number) => (
                <div key={reply.uuid}>
                  <FlexWidth100>
                    <CommentAvatar
                      url={reply.writer.profileImageUrl}
                      name={reply.writer.displayName}
                    />
                    <CommentTextWrap>
                      {profile?.uuid === reply.writer.uuid && <MoreIcon />}
                      <CommentMetaRow>
                        <CommentWriter>{reply.writer.displayName}</CommentWriter>
                        <CommentTime>{communintyDateFormat(reply?.createdAt)}</CommentTime>
                      </CommentMetaRow>
                      <CommentContent>{reply.content}</CommentContent>
                    </CommentTextWrap>
                  </FlexWidth100>
                  <MarginBox
                    onClick={() => {
                      setModal({
                        id: 'unAuthorized',
                        visible: true,
                      });
                    }}
                  >
                    답글쓰기
                  </MarginBox>
                  {
                    // 답변의 답글
                    reply.nestedReplies.length > 0 &&
                      reply.nestedReplies.map((nestReply: NestReplies) => (
                        <FlexMargin
                          style={{
                            marginBottom: 20,
                          }}
                          key={nestReply.uuid}
                        >
                          <CommentAvatar
                            url={nestReply.writer.profileImageUrl}
                            name={nestReply.writer.displayName}
                          />
                          <CommentTextWrap>
                            {profile?.uuid === nestReply.writer.uuid && (
                              <MoreIcon
                                onClick={() => {
                                  setUpdate(nestReply.writer.uuid);
                                }}
                              />
                            )}
                            <CommentMetaRow>
                              <CommentWriter>{nestReply.writer.displayName}</CommentWriter>
                              <CommentTime>{communintyDateFormat(nestReply?.createdAt)}</CommentTime>
                            </CommentMetaRow>
                            <CommentContent>{nestReply.content}</CommentContent>
                          </CommentTextWrap>
                        </FlexMargin>
                      ))
                  }
                </div>
              ))
          }
          {/* 디자인: 입력창은 목록 하단 */}
          <CommentInputRow>
            <CommentInput
              value={commentStr}
              placeholder={'따뜻한 한마디를 남겨주세요'}
              onChange={(e) => onChangeText(false, e.target.value)}
            />
            <CommentButton
              isActive={commentStr.length >= 1}
              onClick={() => {
                if (commentStr.length >= 1) {
                  setModal({
                    id: 'unAuthorized',
                    visible: true,
                  });
                }
              }}
            >
              등록
            </CommentButton>
          </CommentInputRow>
        </CommentContainer>
      )}
    </>
  );
};

/* 디자인(Pple Blood Request 응원 댓글 카드) */
const CommentContainer = styled.section`
  border-radius: 20px;
  background-color: #ffffff;
  border: 1px solid #ededed;
  padding: 28px 32px;
  margin-top: 16px;
  @media ${devices.tablet} {
    padding: 20px 16px;
    border-radius: 16px;
  }
`;

const FlexWidth100 = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;
`;

const CommentHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 16px;
`;

/** 디자인: 이름과 시간이 한 줄에 붙는다 */
const CommentMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/** 디자인: 입력창은 댓글 목록 아래에 위치 */
const CommentInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-top: 4px;
`;
const CommentHeaderTitle = styled.span`
  font-family: 'Var', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #222222;
`;
const CommentHeaderCount = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #ff4651;
`;

const FlexMargin = styled.div`
  display: flex;
  margin-left: 50px;
`;

const CommentInput = styled.input`
  flex: 1;
  height: 46px;
  background-color: #ffffff;
  border: 1px solid #e6e6e6;
  border-radius: 23px;
  padding: 0 18px;
  width: 100%;
  font-family: 'Regular', sans-serif;
  font-size: 14.5px;
  color: #222222;
  outline: none;
  &::placeholder {
    color: #b7b7b7;
  }
`;

const CommentButton = styled.button<{ isActive: boolean }>`
  flex: none;
  height: 46px;
  padding: 0 22px;
  border-radius: 23px;
  border: none;
  background-color: ${({ isActive }) => (isActive ? '#FF4651' : '#F4F4F4')};
  color: ${({ isActive }) => (isActive ? '#ffffff' : '#B7B7B7')};
  margin-left: 8px;
  cursor: ${({ isActive }) => (isActive ? 'pointer' : 'default')};
  font-family: 'Regular', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.6px;
  white-space: nowrap;
`;

const Line = styled.div`
  height: 1px;
  background: #f5f5f5;
  border: none;
  margin: 14px 0;
`;

/* 디자인: 아바타 옆 본문 영역 — 배경 없이 [이름·시간] / 본문 세로 배치 */
const CommentTextWrap = styled.div`
  background-color: transparent;
  flex: 1;
  min-width: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
  > div {
    margin: 0;
  }
  position: relative;
`;

const CommentWriter = styled.div`
  color: #222222;
  font-size: 14px;
  font-weight: 700;
  font-family: 'Var', sans-serif;
  line-height: normal;
`;

const CommentTime = styled.div`
  color: #b7b7b7;
  font-family: 'Var';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: -0.36px;
`;

const CommentContent = styled.div`
  color: #444444;
  font-family: 'Regular', sans-serif;
  font-size: 14.5px;
  font-weight: 400;
  line-height: 23px;
  letter-spacing: -0.3px;
  resize: none;
  border: none;
  width: 100%;
  outline: none;
`;

const MoreIcon = styled(AiOutlineMore)`
  & > path {
    fill: ${({ theme }) => theme.commonColor.gray005};
  }
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
  width: 18px;
  height: 18px;
`;

const MarginBox = styled.span`
  display: inline-block;
  margin: 10px 0 10px 50px;
  color: ${({ theme }) => theme.commonColor.gray005};
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  letter-spacing: -0.36px;
  font-family: 'Var';
  cursor: pointer;
`;

const EmptyProfile = styled.div`
  background-color: #eee;
  border-radius: 50%;
  margin-right: 10px;
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
`;

const Profile = styled.img`
  border-radius: 50%;
  margin-right: 10px;
  object-fit: cover;
  overflow: hidden;
  width: 38px;
  height: 38px;
  min-width: 38px;
  min-height: 38px;
`;
/** 프로필 이미지가 없으면 디자인처럼 이니셜 아바타로 대체한다. */
const InitialAvatar = styled.div<{ tint: string; ink: string }>`
  width: 38px;
  height: 38px;
  min-width: 38px;
  flex: none;
  margin-right: 10px;
  border-radius: 19px;
  background: ${({ tint }) => tint};
  color: ${({ ink }) => ink};
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const CommentAvatar = ({
  url,
  name,
}: {
  url?: string | null;
  name?: string;
}) => {
  if (url) return <Profile src={url} alt="profile" />;
  const t = avatarTint(name || 'u');
  return (
    <InitialAvatar tint={t.tint} ink={t.ink}>
      {avatarInitial(name)}
    </InitialAvatar>
  );
};

export default Comment;
